import React, { useState, useEffect } from 'react';
import { CalendarDaysIcon, PlusIcon, CogIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import CommonPageHeader from '../components/common/CommonPageHeader';
import BookingLinkGenerator from '../components/appointments/BookingLinkGenerator';
import AppointmentList from '../components/appointments/AppointmentList';
import { BookingSettings, Appointment } from '../types/appointments';
import { appointmentService as AppointmentService } from '../services/appointmentService';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from '../components/common/LoadingScreen';
import Modal from '../components/common/Modal';
import BookingSettingsForm from '../components/appointments/BookingSettingsForm';

const AppointmentBooking: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'appointments' | 'settings'>('appointments');
  const [settings, setSettings] = useState<BookingSettings[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingSettings, setEditingSettings] = useState<BookingSettings | null>(null);

  // Beállítások betöltése
  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.organizationId) return;

      try {
        const bookingSettings = await AppointmentService.getBookingSettings(user.organizationId);
        setSettings(bookingSettings);
      } catch (error) {
        console.error('Error loading booking settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user?.organizationId]);

  // Foglalások betöltése
  useEffect(() => {
    const loadAppointments = async () => {
      if (!user?.organizationId || activeTab !== 'appointments') return;

      setAppointmentsLoading(true);
      try {
        const appointmentList = await AppointmentService.getAppointments(user.organizationId);
        setAppointments(appointmentList);
      } catch (error) {
        console.error('Error loading appointments:', error);
      } finally {
        setAppointmentsLoading(false);
      }
    };

    loadAppointments();
  }, [user?.organizationId, activeTab]);

  const handleSettingsChange = () => {
    // Újratöltjük a beállításokat
    if (user?.organizationId) {
      AppointmentService.getBookingSettings(user.organizationId)
        .then(setSettings)
        .catch(console.error);
    }
  };

  const handleCreateSettings = () => {
    setEditingSettings(null);
    setShowSettingsModal(true);
  };

  const handleEditSettings = (setting: BookingSettings) => {
    setEditingSettings(setting);
    setShowSettingsModal(true);
  };

  const handleUpdateAppointmentStatus = async (appointmentId: string, status: Appointment['status']) => {
    try {
      await AppointmentService.updateAppointmentStatus(appointmentId, status);
      // Frissítjük a helyi listát
      setAppointments(prev => 
        prev.map(apt => 
          apt.id === appointmentId ? { ...apt, status } : apt
        )
      );
    } catch (error) {
      console.error('Error updating appointment status:', error);
    }
  };

  const handleDeleteAppointment = async (appointmentId: string) => {
    try {
      // TODO: Implement delete appointment in service
      console.log('Delete appointment:', appointmentId);
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  const handleUpdateAppointment = async (updatedAppointment: Appointment) => {
    try {
      await AppointmentService.updateAppointment(
        updatedAppointment.id!, 
        updatedAppointment, 
        user?.email || undefined
      );
      setAppointments(prev => 
        prev.map(apt => apt.id === updatedAppointment.id ? updatedAppointment : apt)
      );
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-4">
      {/* Fejléc */}
      <CommonPageHeader
        title="Időpontfoglalás"
        subtitle="Kezeld az időpontjaidat és oszd meg a foglalási linkjeidet"
        icon={CalendarDaysIcon}
      >
        <button onClick={handleCreateSettings} className="crm-btn crm-btn-primary">
          <PlusIcon className="h-4 w-4" />
          <span>Új beállítás</span>
        </button>
      </CommonPageHeader>

      {/* Fülek */}
      <div className="border-b border-[var(--crm-border)]">
        <nav className="flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Tabs">
          {([
            { id: 'appointments', label: 'Foglalások', icon: ListBulletIcon, count: appointments.length },
            { id: 'settings', label: 'Beállítások & linkek', icon: CogIcon, count: 0 },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-3 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count > 0 && <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-[11px] tabular-nums bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{tab.count}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Tartalom */}
      {activeTab === 'appointments' ? (
        /* Foglalások lista - teljes szélesség */
        <div>
          <AppointmentList
            appointments={appointments}
            settings={settings}
            onUpdateStatus={handleUpdateAppointmentStatus}
            onDeleteAppointment={handleDeleteAppointment}
            onUpdateAppointment={handleUpdateAppointment}
            loading={appointmentsLoading}
          />
        </div>
      ) : (
        /* Beállítások - korlátozott szélesség */
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Bal oldal - Statisztikák */}
            <div className="lg:col-span-1">
              <div className="crm-card p-4">
                <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50 mb-3">
                  Gyors áttekintés
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Aktív beállítások:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {settings.filter(s => s.isActive).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Összes beállítás:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {settings.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Mai foglalások:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {appointments.filter(apt => {
                        const today = new Date();
                        const aptDate = apt.appointmentDate;
                        return aptDate.toDateString() === today.toDateString();
                      }).length}
                    </span>
                  </div>
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={handleCreateSettings} className="crm-btn crm-btn-secondary w-full">
                      <CogIcon className="h-4 w-4" />
                      <span>Új beállítás</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Jobb oldal - Link generátor */}
            <div className="lg:col-span-2">
              <BookingLinkGenerator
                settings={settings}
                onSettingsChange={handleSettingsChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Beállítások modal */}
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        title={editingSettings ? 'Beállítás szerkesztése' : 'Új foglalási beállítás'}
        size="5xl"
        noPadding
      >
        <BookingSettingsForm
          settings={editingSettings}
          onSave={(savedSettings) => {
            handleSettingsChange();
            setShowSettingsModal(false);
          }}
          onCancel={() => setShowSettingsModal(false)}
        />
      </Modal>
    </div>
  );
};


export default AppointmentBooking;
